import React from "react";
import { useQuery } from "@apollo/client";
import { gql } from "../../__generated__";
import { Store } from "../../__generated__/graphql";
import { useNavigate } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";
import { getDownloadLink } from "../../utils/app-download-link";
import { StoreCardForWelcome } from "../../components/store-card/store-card-for-welcome";

const WELCOME_STORES = gql(`
  query WelcomeStores {
    welcomeStores {
      id
      title
      address
      createdAt
      updatedAt
      img
      contactNumber
      closingHours
      reserveTime
      pack {
        id
        quantity
        price
        discountPrice
        userPrice
        quantity
        currentCount
        isOverTodayPickupTime
        isTodayEnabled
        isTomorrowEnabled
      }
      likes {
        id
        storeId
      }
    }
  }
`);

export default function Welcome() {
  const navigate = useNavigate();
  const { data, loading } = useQuery(WELCOME_STORES, {
    fetchPolicy: "cache-first",
  });

  function onClickStore(store: Store) {
    track("매장 클릭", {
      매장명: store.title,
      어디서: "welcome",
    });

    navigate(`/store/${store.id}`); // 해당 매장 상세페이지로 이동
  }

  const handleDownloadClick = () => {
    track("앱 다운로드 클릭", { 어디서: "welcome" });
    window.open(getDownloadLink(), "_blank", "noopener,noreferrer");
  };

  const renderStoreCard = (store: Store) => {
    if (!store) return null;

    // 매장과 가격만 렌더링
    return (
      <div className="w-[90%] flex-shrink-0" key={store.id}>
        <StoreCardForWelcome
          title={store.title}
          discountPrice={store.pack?.userPrice ?? 6000}
          price={store.pack?.price ?? 6000}
          img={store.img as string}
          onClick={() => onClickStore(store as Store)}
          address={store.address ?? ""}
        />
      </div>
    );
  };

  return (
    <div className="relative w-full h-screen bg-white pb-20 flex flex-col">
      {/* 헤더 섹션 */}
      <div className="px-4 pt-10 pb-6">
        <div className="flex items-center mb-2">
          <div className="w-1 h-20 bg-blue-500 mr-3"></div>
          <h1 className="text-4xl font-bold text-black">
            얼마나
            <br /> 저렴한가요?
          </h1>
        </div>
        <p className="text-gray-900 text-lg font-medium text-center pt-4">
          실제 고객들이 경험하고 있는
          <br /> 마감할인 가격을 확인해보세요!
        </p>
      </div>

      {/* 가로 스크롤 매장 리스트 */}
      <div className="flex-1 flex flex-col justify-center pb-24">
        <div className="flex overflow-x-scroll gap-4 pb-4 pl-4 w-full">
          {loading
            ? // 로딩 스켈레톤
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="w-[90%] flex-shrink-0">
                  <div className="bg-gray-200 rounded-lg h-48 animate-pulse"></div>
                </div>
              ))
            : data?.welcomeStores?.map((store) =>
                renderStoreCard(store as Store)
              )}
        </div>

        {/* 하단 네비게이션 */}
        <div className="px-4 pt-2">
          <div className="flex justify-center items-center gap-2 text-gray-500">
            <span className="rounded-full bg-gray-100 font-bold w-7 h-7 flex items-center justify-center">
              ←
            </span>
            <span className="text-lg font-medium">
              스와이프하여 더 많은 매장 보기
            </span>
            <span className="rounded-full bg-gray-100 font-bold w-7 h-7 flex items-center justify-center">
              →
            </span>
          </div>
        </div>
      </div>

      {/* 구매하기 버튼 - 하단 고정 */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-8 bg-white">
        <button
          className="w-full bg-[#1562fc] text-white font-bold py-4 rounded-xl text-xl"
          onClick={handleDownloadClick}
        >
          구매하기
        </button>
      </div>
    </div>
  );
}
